// src/MapPage.jsx
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// clear default icon URLs so our DivIcon shows instead
delete L.Icon.Default.prototype._getIconUrl;

const DATADOCK_API_KEY = 'bc6db4553d2ec8158bba7df424411522';

export default function MapPage() {
  const [markers, setMarkers] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ships`)
      .then(res => {
        if (!res.ok) throw new Error(`DB fetch failed: ${res.status}`);
        return res.json();
      })
      .then(ships => {
        const valid = ships.filter(s => s.mmsi || s.imoNumber);
        return Promise.all(
          valid.map(ship => {
            const id = ship.mmsi || ship.imoNumber;
            return fetch(
              `https://datadocked.com/api/vessels_operations/get-vessel-info` +
              `?api_key=${DATADOCK_API_KEY}` +
              `&imo_or_mmsi=${encodeURIComponent(id)}`
            )
              .then(r => {
                if (!r.ok) throw new Error(`Vessel ${id} fetch ${r.status}`);
                return r.json();
              })
              .then(json => {
                const info = (json.detail && json.detail.vesselInfo)
                  ? json.detail.vesselInfo
                  : json.detail;
                if (!info || !info.latitude || !info.longitude) {
                  throw new Error(`No coords for ${id}`);
                }

                // parse coordinates
                const [latStr] = info.latitude.split('/');
                const [lonStr] = info.longitude.split('/');
                const lat = parseFloat(latStr);
                const lon = parseFloat(lonStr);
                if (isNaN(lat) || isNaN(lon)) {
                  throw new Error(`Invalid coords for ${id}`);
                }

                // parse course
                const courseDeg = parseFloat(info.course) || 0;

                // color by DB vesselType
                const typeKey = (ship.vesselType || '').toLowerCase();
                const colorMap = {
                  'bulk carrier':    'green',
                  'tanker':          'red',
                  'container ship':  'blue',
                  'offshore':        'orange',
                  'car carrier':     'purple',
                  'ro-ro':           'brown',
                  'ferry':           'teal',
                  'fpso':            'magenta',
                  'lng':             'navy',
                  'lpg':             'cyan',
                };
                const fillColor = colorMap[typeKey] || 'gray';

                // build rotated arrow
                const icon = L.divIcon({
                  className: '',
                  iconSize:   [30, 30],
                  iconAnchor: [15, 15],
                  html: `
                    <svg
                      width="30" height="30"
                      viewBox="0 0 30 30"
                      style="transform: rotate(${courseDeg}deg);
                             transform-origin: center;"
                    >
                      <polygon
                        points="15,0 30,30 15,25 0,30"
                        fill="${fillColor}"
                      />
                    </svg>
                  `
                });

                return {
                  info,
                  position: [lat, lon],
                  icon,
                  vesselType: ship.vesselType  // <-- carry it through
                };
              })
              .catch(err => {
                console.warn('Skipping', id, err.message);
                return null;
              });
          })
        );
      })
      .then(results => {
        setMarkers(results.filter(m => m));
      })
      .catch(err => {
        console.error(err);
        setMarkers([]); 
      });
  }, []);

  if (markers === null) {
    return <p>Loading vessels…</p>;
  }
  if (!markers.length) {
    return <p>No vessel positions available.</p>;
  }

  const positions = markers.map(m => m.position);

  return (
    <MapContainer
      bounds={positions}
      style={{ height: '100vh', width: '100%' }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />

      {markers.map(({ info, position, icon, vesselType }) => (
        <Marker
          key={info.mmsi || info.imo}
          position={position}
          icon={icon}
        >
          <Popup minWidth={200}>
            <h5>{info.name}</h5>
            <p><strong>MMSI:</strong> {info.mmsi || '—'}</p>
            <p><strong>IMO:</strong> {info.imo || '—'}</p>
            <p><strong>Call Sign:</strong> {info.callsign}</p>
            <p><strong>Type:</strong> {vesselType}</p>
            <p><strong>Country:</strong> {info.country} ({info.countryIso})</p>
            <p><strong>Speed:</strong> {info.speed}</p>
            <p><strong>Course:</strong> {info.course}</p>
            <p><strong>Destination:</strong> {info.destination || 'N/A'}</p>
            <p><strong>Last Port:</strong> {info.lastPort || 'N/A'}</p>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
